import {
  Dispatch,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { VegaChartDto } from '@asset-metrix/portal-gateway-client-js';
import {
  ErrorMessage,
  useHeightPerEntry,
  useSignal,
  VegaChart as VegaChartUi,
  Widget,
  WidgetWithPaginator,
} from '@asset-metrix/portal-ui';
import { noop } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Spec, View } from 'vega';

import {
  fetchDatatableData,
  FetchDatatableDataQueryKey,
} from '../../api/portalGateway/datatableData';
import { fetchPage, PageQueryKey } from '../../api/portalGateway/page';
import {
  useCustomTableFilterQuery,
  useGlobalFilterLoaded,
  usePageFilterQuery,
} from '../../hooks/filter';
import { useStore } from '../../store';
import {
  getCustomPageFilterInstanceId,
  useCombinedPageFilterSelections,
} from '../../store/hooks/useCombinedPageFilterSelections';
import { useSelections } from '../../store/hooks/useSelections';
import { useIsDashboardDraftMode } from '../../store/states/dashboardEditMode';
import { useStakeAdjustmentStore } from '../../store/states/stakeAdjustment';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_VEGA_CHART_PAGE_SIZE,
} from '../../utils/constants';
import { extractErrorMessage } from '../../utils/errorMessage';
import { getTableFilterInstanceId } from '../../utils/filterInstanceId';
import { prependRoleId } from '../../utils/prependRoleId';
import type { DashboardFilterSelections } from '../customizableDashboard/dashboardTileFilters';
import { useDashboardContentEditActions } from '../customizableDashboard/useDashboardContentEditActions';
import { useDashboardTileFilters } from '../customizableDashboard/useDashboardTileFilters';
import { CustomTableFilters } from '../filter/customTableFilter/CustomTableFilters';
import { PageFilters } from '../filter/PageFilters';
import { WidgetFilterContainer } from '../filter/WidgetFilterContainer';
import { StakeAdjustmentToggle } from '../page/StakeAdjustmentToggle';
import { useContentEditActions } from '../page/useContentEditActions';

import { ExtractedBinding } from './inputBinding/extraction';
import { VegaControlAction, VegaState } from './inputBinding/vegaControlState';
import { useFooterChartData } from './chartDataTransformHook';
import { usePageBoundaries } from './paginationHooks';
import {
  BaseUseVegaChartActionProps,
  useVegaChartActions,
} from './useVegaChartActions';
import { VegaChartSignalControls } from './VegaChartSignalControls';

import styles from './vegaChart.module.scss';

export type VegaChartContentProps = {
  collapsible: boolean;
  controlDispatch: Dispatch<VegaControlAction>;
  controlState: VegaState;
  bindings: ExtractedBinding[];
  dataTestId?: string;
  enableScrollElementToTop?: boolean;
  entity: VegaChartDto;
  isFullscreen: boolean;
  labelData: object;
  pageFilterInstanceId: string;
  pageId: string;
  dashboardId?: string | null;
  dashboardContentId?: string;
  contentSourcePageId?: string;
  /** True while this tile's active custom-filter list is still loading. */
  isFilterCustomizationLoading?: boolean;
  savedPageFilterSelections?: DashboardFilterSelections;
  savedCustomTableFilterSelections?: DashboardFilterSelections;
  customPageFilterStoreId?: string | null;
  showPageFilter?: boolean;
  spec: Spec;
  withPagination: boolean;
  widgetInstanceId: string;
};

const getFullScreenHeight = (heightPerEntry: number, itemCount?: number) =>
  itemCount ? 100 + itemCount * heightPerEntry : undefined;

const useOnReadyControlState = (
  state: VegaState,
  viewRef: RefObject<View | null>
) => {
  const signal = useSignal(viewRef);
  return useCallback(() => {
    for (const [signalName, signalState] of Object.entries(state)) {
      switch (signalState?.type) {
        case 'radio':
          signal(signalName, signalState.value);
          globalThis.dispatchEvent(new CustomEvent('resizevegachart'));
      }
    }
  }, [signal, state]);
};

export function VegaChartContent({
  activeCustomFilterIds,
  bindings,
  customPageFilterStoreId,
  collapsible,
  controlDispatch,
  controlState,
  dataTestId,
  enableScrollElementToTop,
  entity,
  isFullscreen,
  labelData,
  pageFilterInstanceId,
  pageId,
  dashboardId = null,
  dashboardContentId,
  contentSourcePageId,
  isFilterCustomizationLoading = false,
  savedPageFilterSelections,
  savedCustomTableFilterSelections,
  showPageFilter = false,
  spec,
  title,
  widgetId,
  withPagination,
  widgetInstanceId,
  ...useVegaChartActionProps
}: VegaChartContentProps & BaseUseVegaChartActionProps) {
  const { t } = useTranslation();
  const vegaViewRef = useRef<View>(null);
  const onReadyControlState = useOnReadyControlState(controlState, vegaViewRef);

  const heightPerEntry = useHeightPerEntry(spec, vegaViewRef);

  const isDashboardTile = dashboardContentId != null;
  const isDashboardEditMode = useIsDashboardDraftMode(dashboardId);

  const { data: page } = useQuery({
    queryKey: prependRoleId<PageQueryKey>(['page', pageId ?? 'unknown']),
    queryFn: fetchPage,

    enabled: showPageFilter && !isDashboardTile,
  });

  const customPageFilterInstanceId =
    getCustomPageFilterInstanceId(pageFilterInstanceId);
  const tableFilterInstanceId = getTableFilterInstanceId(
    widgetInstanceId,
    pageFilterInstanceId
  );

  const activeCustomFilterConfigs = useMemo(
    () =>
      entity.config.customFilter
        .filter((f) => activeCustomFilterIds.includes(f.id))
        .map((f) => f.filterConfig),
    [activeCustomFilterIds, entity.config.customFilter]
  );

  const globalFilterLoaded = useGlobalFilterLoaded();
  const customPageFilterLoaded = useStore(
    (state) => state.filterQueryLoaded[customPageFilterInstanceId] ?? false
  );
  const stakeAdjusted = useStakeAdjustmentStore((state) =>
    entity.config.enableStakeAdjustment
      ? state.stakeAdjusted[pageFilterInstanceId]
      : undefined
  );

  const pageFilterQuery = usePageFilterQuery(
    pageId,
    pageFilterInstanceId,
    undefined
  );
  const customTableFilterQuery = useCustomTableFilterQuery(
    widgetId,
    tableFilterInstanceId,
    pageFilterInstanceId,
    isDashboardTile ? [] : activeCustomFilterConfigs
  );
  const columnConfigs = useMemo(
    () =>
      entity.config.customFilter.map((filter) => ({
        id: filter.id,
        customFilter: filter.filterConfig,
      })),
    [entity.config.customFilter]
  );
  const dashboardFilters = useDashboardTileFilters({
    enabled: isDashboardTile,
    editable: isDashboardEditMode,
    contentSourcePageId,
    tileInstanceId: dashboardContentId ?? widgetId,
    savedPageFilterSelections,
    savedCustomTableFilterSelections,
    pageFilterInstanceId,
    tableFilterInstanceId,
    widgetId,
    activeCustomFilterIds,
    customFilterColumns: columnConfigs,
    isFilterCustomizationLoading,
  });

  const combinedFilterSelections =
    useCombinedPageFilterSelections(pageFilterInstanceId);
  const tableFilterSelection = useSelections(tableFilterInstanceId);
  const requestPageFilter = isDashboardTile
    ? dashboardFilters.pageFilterSelections
    : combinedFilterSelections;
  const requestTableFilter = isDashboardTile
    ? dashboardFilters.customTableFilterSelections
    : tableFilterSelection;
  const requestStakeAdjusted = isDashboardTile ? undefined : stakeAdjusted;

  const stakeAdjustmentReady =
    isDashboardTile ||
    !entity.config.enableStakeAdjustment ||
    stakeAdjusted !== undefined;

  const filtersAreLoaded = isDashboardTile
    ? globalFilterLoaded && dashboardFilters.ready
    : globalFilterLoaded &&
      pageFilterQuery.isSuccess &&
      customPageFilterLoaded &&
      customTableFilterQuery.isSuccess &&
      stakeAdjustmentReady;

  const isGroupDataConfigured = entity.config.vegaChartDataType === 'groupData';
  const isGroupOrFooterDataConfigured =
    isGroupDataConfigured || entity.config.vegaChartDataType === 'footerData';

  const { data, isLoading: isDataLoading } = useQuery({
    queryKey: prependRoleId<FetchDatatableDataQueryKey>([
      'datatables',
      { dataSourceType: 'datatable' },
      { pageId },
      { widgetId },
      {
        page: 0,
        pageSize: DEFAULT_VEGA_CHART_PAGE_SIZE,
        pageFilter: requestPageFilter,
        tableFilter: requestTableFilter,
        sortItems: [],
        stakeAdjusted: requestStakeAdjusted,
      },
    ]),
    queryFn: fetchDatatableData,
    enabled:
      filtersAreLoaded &&
      entity.config.hasDataTable &&
      !isGroupOrFooterDataConfigured,
  });

  const {
    transformedData,
    isLoading: isFooterDataLoading,
    error,
  } = useFooterChartData(
    widgetId,
    pageFilterInstanceId,
    tableFilterInstanceId,
    filtersAreLoaded &&
      entity.config.hasDataTable &&
      isGroupOrFooterDataConfigured,
    isGroupDataConfigured,
    entity.config,
    requestStakeAdjusted,
    isDashboardTile
      ? {
          pageFilter: requestPageFilter,
          tableFilter: requestTableFilter,
        }
      : undefined
  );

  const rawData = useMemo(() => {
    if (entity.config.hasDataTable) {
      return isGroupOrFooterDataConfigured ? transformedData : data?.data.items;
    }

    const specRawData = spec.data?.find((d) => d.name === 'rawData');

    if (specRawData && 'values' in specRawData) {
      return specRawData.values;
    }
  }, [
    entity.config.hasDataTable,
    spec.data,
    isGroupOrFooterDataConfigured,
    transformedData,
    data?.data.items,
  ]);

  const {
    currentPage,
    onReadyCallback: onReadyPageBoundaries,
    setCurrentPage,
    itemCount,
  } = usePageBoundaries(
    withPagination,
    isFullscreen,
    vegaViewRef,
    spec,
    rawData,
    pageFilterInstanceId,
    widgetInstanceId
  );

  useEffect(() => {
    setTimeout(() =>
      globalThis.dispatchEvent(new CustomEvent('resizevegachart'))
    );
  }, [rawData]);

  const Component = withPagination ? WidgetWithPaginator : Widget;

  const widgetActions = useVegaChartActions({
    config: entity.config,
    activeCustomFilterIds,
    bindings,
    controlState,
    labelData,
    title,
    widgetId,
    pageFilter: requestPageFilter,
    tableFilter: requestTableFilter,
    vegaViewRef,
    stakeAdjusted: requestStakeAdjusted,
    ...useVegaChartActionProps,
  });
  const pageEditActions = useContentEditActions(
    pageId,
    widgetId,
    widgetActions
  );
  const actions = useDashboardContentEditActions(
    dashboardId,
    dashboardContentId ?? widgetId,
    pageEditActions
  );

  const vegaData = useMemo(
    () => ({
      labelData,
      rawData: rawData ?? [],
    }),
    [labelData, rawData]
  );

  const onReady = useCallback(
    (newView: View) => {
      onReadyPageBoundaries(newView).catch((error) =>
        console.error('Vega chart pagination setup failed', error)
      );
      onReadyControlState();
    },
    [onReadyControlState, onReadyPageBoundaries]
  );

  if (
    (isGroupDataConfigured && !entity.config.groupedColumnForChart) ||
    error
  ) {
    const errorMessage = error
      ? extractErrorMessage(error)
      : t('app.vegaChartWidget.groupingError');

    return (
      <Widget title={title} dataTestId={dataTestId}>
        <ErrorMessage>{errorMessage}</ErrorMessage>
      </Widget>
    );
  }

  const isLoading = !filtersAreLoaded || isDataLoading || isFooterDataLoading;

  return (
    <Component
      dataTestId={dataTestId}
      title={title}
      collapsible={collapsible}
      enableScrollElementToTop={enableScrollElementToTop}
      actions={actions}
      activePage={currentPage}
      activePaginationSize={DEFAULT_PAGE_SIZE}
      availablePaginationSizes={[DEFAULT_PAGE_SIZE]}
      onPageChange={setCurrentPage}
      onPaginationSizeChange={noop}
      totalEntries={itemCount}
      paginatorDisabled={isLoading}
    >
      {isDashboardTile ? (
        dashboardFilters.filterBar != null && (
          <WidgetFilterContainer>
            {dashboardFilters.filterBar}
          </WidgetFilterContainer>
        )
      ) : (
        <WidgetFilterContainer>
          {showPageFilter && page != null ? (
            <PageFilters
              config={page.data.pageFilter}
              filterKey={pageFilterInstanceId}
              customPageFilterStoreId={customPageFilterStoreId}
              pageId={pageId}
              embedded={true}
            />
          ) : null}
          {activeCustomFilterIds.length > 0 && (
            <CustomTableFilters
              activeCustomFilterIds={activeCustomFilterIds}
              columnConfigs={columnConfigs}
              pageFilterInstanceId={pageFilterInstanceId}
              tableFilterInstanceId={tableFilterInstanceId}
              widgetId={widgetId}
            />
          )}
          {isFullscreen &&
            entity.config.enableStakeAdjustment &&
            page != null && (
              <StakeAdjustmentToggle
                filterKey={pageFilterInstanceId}
                defaultEnabled={
                  page.data.enableStakeAdjustmentByDefault ?? false
                }
              />
            )}
        </WidgetFilterContainer>
      )}
      {bindings.length > 0 && (
        <VegaChartSignalControls
          bindings={bindings}
          controlDispatch={controlDispatch}
          controlState={controlState}
          // vega specs could potentially contain label data that is no array
          labelData={Array.isArray(labelData) ? labelData : []}
          vegaViewRef={vegaViewRef}
        />
      )}
      <VegaChartUi
        className={styles['vegaChart']}
        spec={spec}
        data={vegaData}
        onReadyCallback={onReady}
        heightfactor={entity.config.heightfactor}
        isFullscreen={isFullscreen}
        loading={isLoading}
        vegaViewRef={vegaViewRef}
        minHeight={
          isFullscreen && heightPerEntry
            ? getFullScreenHeight(heightPerEntry, itemCount)
            : undefined
        }
      />
    </Component>
  );
}
